import { Check, Lock01, LockUnlocked01 } from "@untitledui/icons"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"

import {
  Tooltip,
  TooltipTrigger,
} from "@/components/untitled-ui/base/tooltip/tooltip"
import { DUR, MS } from "@/constants/coaching-motion"
import type { DoorOpenedPayload } from "@/types/conversation"

// The live + replay "doors opened" progress counter. The trainee's whole job is
// framed as "open doors to getting help" (the GP-held disclosures), so this makes
// that progress literal and persistent — the same four-door model the debrief
// tallies.
//
// Doors are discovered, not previewed: a door stays a nameless lock until it
// opens, then its label slides in and a tooltip names — retroactively — what the
// trainee did and what care it unlocked. During the intro the parent reveals the
// (still-locked, still-nameless) doors gradually via `revealCount`, paced to the
// coaching cards. Substantive (information-bearing) doors play a tick; the
// borderline `not_a_waste` (ordinary GP warmth) fills silently.

// Canonical order + labels + the retroactive "what you did → what it unlocked"
// note. Firewall-safe: observable narrative facts only, never grades.
const DOORS: {
  id: DoorOpenedPayload["id"]
  label: string
  substantive: boolean
  note: string
}[] = [
  {
    id: "not_a_waste",
    label: "You're not wasting their time",
    substantive: false,
    note: "By describing how you actually feel, you showed your GP this is exactly what they're for.",
  },
  {
    id: "confidentiality",
    label: "What you share stays private",
    substantive: true,
    note: "You checked who finds out — so now you know your honesty is safe here.",
  },
  {
    id: "treatment_options",
    label: "What help is available",
    substantive: true,
    note: "You asked what's possible — so your GP could lay out the real options, including treatment.",
  },
  {
    id: "referral_pathway",
    label: "Where to go next",
    substantive: true,
    note: "You said what you'd like to happen — so you're leaving with a concrete next step.",
  },
]

// Door id → label, for surfaces that name a single door (e.g. the example player).
export const DOOR_LABELS: Record<string, string> = Object.fromEntries(
  DOORS.map((d) => [d.id, d.label]),
)

export interface DoorsTrackerProps {
  // The door_opened events seen so far (live: filtered from the stream; replay:
  // folded into ConversationState). Order/duplication tolerated — keyed by id.
  opened: DoorOpenedPayload[]
  // How many doors to show, from the start of DOORS. Used during the intro to
  // reveal the (locked, nameless) doors gradually, paced to the coaching cards.
  // Defining it switches to "teaching" presentation (no opened-count header).
  // Out-of-range values are clamped by slice. Omit for the live/replay default
  // (all doors).
  revealCount?: number
  // "bare" drops the panel chrome so the tracker can sit inside the takeover card.
  tone?: "panel" | "bare"
  // Whether a freshly-opened door auto-announces its tooltip once. Off for replay
  // (the scrubber drives it) so playback stays calm — hover/focus still works.
  announce?: boolean
  className?: string
}

export default function DoorsTracker({
  opened,
  revealCount,
  tone = "panel",
  announce = true,
  className,
}: DoorsTrackerProps) {
  const reduceMotion = useReducedMotion()
  const openedById = new Map(opened.map((d) => [d.id, d]))
  const visibleDoors = DOORS.slice(0, revealCount ?? DOORS.length)
  const openedCount = DOORS.filter((d) => openedById.has(d.id)).length
  // `revealCount` is supplied only by the intro's gradual reveal; treat its
  // presence as "teaching mode" (hide the count so the total isn't spoiled).
  const teaching = revealCount !== undefined

  // "A label introduces itself": when a door first opens, briefly auto-announce
  // its tooltip, then leave it hover/focus reachable. Controlled tooltips (isOpen
  // is always a boolean for an opened door) so hover/focus and the auto-announce
  // share one source of truth. Suppressed under reduced motion.
  const [openTips, setOpenTips] = useState<Set<string>>(() => new Set())
  const announced = useRef<Set<string>>(new Set())
  const setTip = (id: string, open: boolean) =>
    setOpenTips((prev) => {
      const next = new Set(prev)
      if (open) next.add(id)
      else next.delete(id)
      return next
    })

  useEffect(() => {
    if (reduceMotion || !announce) {
      opened.forEach((d) => announced.current.add(d.id))
      return
    }
    const timers: number[] = []
    opened.forEach((d) => {
      if (announced.current.has(d.id)) return
      announced.current.add(d.id)
      setTip(d.id, true)
      timers.push(
        window.setTimeout(() => setTip(d.id, false), MS.tooltipAutoOpen),
      )
    })
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [opened, reduceMotion, announce])

  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-xs ${
        tone === "panel"
          ? "rounded-lg border border-gray-200 bg-white/70 px-4 py-2 dark:border-gray-700 dark:bg-gray-900/40"
          : "justify-center"
      } ${className ?? ""}`}
      role="status"
      aria-label={
        teaching
          ? "Doors to open"
          : `${openedCount} of ${DOORS.length} doors opened`
      }
    >
      {teaching ? (
        <span className="font-semibold text-gray-600 dark:text-gray-300">
          Doors to open
        </span>
      ) : (
        <span className="font-semibold text-gray-700 dark:text-gray-200">
          Doors opened{" "}
          <span className="text-green-700 dark:text-green-400">
            {openedCount}
          </span>
          <span className="text-gray-400">/{DOORS.length}</span>
        </span>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <AnimatePresence initial={false}>
          {visibleDoors.map((door) => {
            const open = openedById.get(door.id)
            const isOpen = open !== undefined
            const substantive = open?.substantive ?? door.substantive
            // Only substantive doors get the celebratory tick; not_a_waste fills
            // quietly (no Check, muted fill) to honour its borderline status.
            const ticks = isOpen && substantive

            // The pill grows (layout) as its label slides in on open.
            const pill = (
              <motion.span
                layout={!reduceMotion}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors ${
                  !isOpen
                    ? "border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400"
                    : ticks
                      ? "border-green-200 bg-green-50 font-medium text-green-800 dark:border-green-800/60 dark:bg-green-900/20 dark:text-green-200"
                      : "border-green-100 bg-green-50/60 text-green-700 dark:border-green-900/40 dark:bg-green-900/10 dark:text-green-300/90"
                }`}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {ticks ? (
                    <motion.span
                      key="tick"
                      initial={
                        reduceMotion ? false : { scale: 0.4, opacity: 0 }
                      }
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="flex"
                    >
                      <Check className="size-3.5 shrink-0 text-green-600 dark:text-green-400" />
                    </motion.span>
                  ) : isOpen ? (
                    <LockUnlocked01
                      key="unlocked"
                      className="size-3.5 shrink-0 text-green-600/80 dark:text-green-400/80"
                    />
                  ) : (
                    <Lock01
                      key="locked"
                      className="size-3.5 shrink-0 text-gray-400"
                    />
                  )}
                </AnimatePresence>

                {/* The label is withheld until the door opens, then fades in
                    while the pill grows around it. */}
                {isOpen && (
                  <motion.span
                    className="whitespace-nowrap"
                    initial={reduceMotion ? false : { opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: reduceMotion ? 0 : DUR.doorReveal,
                      ease: "easeOut",
                    }}
                  >
                    {door.label}
                  </motion.span>
                )}
              </motion.span>
            )

            return (
              <motion.div
                key={door.id}
                layout={!reduceMotion}
                initial={
                  reduceMotion ? false : { opacity: 0, scale: 0.8, y: 4 }
                }
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={
                  reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8 }
                }
                transition={{
                  duration: reduceMotion ? 0 : DUR.doorReveal,
                  ease: "easeOut",
                }}
              >
                {isOpen ? (
                  <Tooltip
                    title={door.label}
                    description={door.note}
                    placement="top"
                    isOpen={openTips.has(door.id)}
                    onOpenChange={(o) => setTip(door.id, o)}
                  >
                    <TooltipTrigger>{pill}</TooltipTrigger>
                  </Tooltip>
                ) : (
                  pill
                )}
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  )
}
